/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { View } from "react-native";

import { IssueCategoryType, Scalars } from "graphql/internal/generated";
import { REPORT_ISSUE } from "graphql/internal/Mutations";
import { Button, FadeInOutView, Text, TextInput } from "UpduoLibrary";
import {
  upduoSecondLightestGray,
  skaleDeactiveGray,
  skalePrimaryBlue,
} from "config/UpduoColors";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import CategoryMenu from "./CategoryMenu";

interface ReportIssueViewProps {
  marginHorizontal?: number;
  sessionId?: null | Scalars["GlobalID"];
  onFinish: () => void;
  alignButton?: "flex-start" | "center" | "flex-end";
}

export default function ReportIssueView({
  marginHorizontal = 32,
  sessionId = null,
  onFinish,
  alignButton = "center",
}: ReportIssueViewProps) {
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [category, setCategory] = useState<IssueCategoryType>();
  const [content, setContent] = useState("");

  const [reportIssue, {}] = useMutation(REPORT_ISSUE, {
    onCompleted: async (result) => {
      console.log("result", result);
      if (!result?.reportIssue?.ok) {
        setButtonDisabled(false);
        throw result;
      }
      UpduoAnalytics.logEvent("user-issue-submit", {
        sessionId,
        category,
        content,
      });
      setIsSubmitted(true);
      onFinish();
    },
    onError: (error) => {
      setButtonDisabled(false);
      console.error("ERROR reportIssue", error);
      UpduoAnalytics.logEvent("app-reportissue-error", {
        error,
      });
    },
  });

  const CHAR_REQUIREMENT = 10;

  return (
    <FadeInOutView style={{ marginHorizontal }}>
      <Text isHeading={true}>{"What kind of issue did you encounter?"}</Text>
      <CategoryMenu
        categoryChoice={category}
        onFinish={(choice: IssueCategoryType) => {
          setCategory(choice);
        }}
      />
      <Text isHeading={true}>
        {"Are there any other details you would like to add?"}
      </Text>
      <TextInput
        style={{
          textAlignVertical: "top",
          borderColor: upduoSecondLightestGray,
          height: 200,
          paddingTop: 20,
          paddingBottom: 20,
          paddingHorizontal: 20,
          borderWidth: 1,
          borderRadius: 8,
          marginTop: 8,
          marginBottom: 24,
        }}
        selectionColor={skalePrimaryBlue}
        placeholderTextColor={skaleDeactiveGray}
        multiline={true}
        onChangeText={(text) => setContent(text)}
        value={content}
        underlineColorAndroid={"transparent"}
        placeholder={"Enter your feedback"}
      />
      <View style={{ alignSelf: alignButton }}>
        <Button
          disabled={
            buttonDisabled || content.length < CHAR_REQUIREMENT || isSubmitted
          }
          onPress={() => {
            setButtonDisabled(true);
            reportIssue({
              variables: {
                reportIssueRequest: {
                  category,
                  content,
                  skalesessionId: sessionId,
                },
              },
            });
          }}
          width={295}
          marginHorizontal={0}
          text={isSubmitted ? "Submitted" : "Submit"}
        />
      </View>
    </FadeInOutView>
  );
}
